import { FileText } from 'lucide-react';
import { useId, useRef, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EsqueletoDeLista } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';

import type { DocumentoNaLista, FinalidadeDoArquivo } from './api';
import {
  abrirArquivoDoDocumento,
  explicarFalha,
  linhaDoArquivo,
  problemasDoArquivo,
  ROTULO_DA_FINALIDADE,
  useAnexarAoDocumento,
  useArquivosDoDocumento,
} from './arquivo';

type Recusa = { readonly erro: string; readonly comoResolver: string } | null;

/**
 * O ARQUIVO DO DOCUMENTO E A PROVA DA REGULARIZAÇÃO, na mesa (0090). Mora na
 * janela que a linha do documento abre: a lista do que já está guardado, e o
 * envio de um arquivo novo para quem tem `documento_de_pessoa.gravar`.
 *
 * Nada se apaga: o documento renovado ganha arquivo novo, e o antigo fica
 * com a validade da época — é o que a fiscalização pergunta depois.
 */
export function ArquivosDoDocumento({ documento, podeEnviar }: { readonly documento: DocumentoNaLista; readonly podeEnviar: boolean }) {
  const base = useId();
  const arquivos = useArquivosDoDocumento(documento.id);
  const anexar = useAnexarAoDocumento();
  const campoDoArquivo = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [descricao, setDescricao] = useState('');
  /* Vencido ou sem data, o que se envia costuma ser a prova da regularização. */
  const [finalidade, setFinalidade] = useState<FinalidadeDoArquivo>(
    documento.estado === 'vencido' || documento.estado === 'sem_data' ? 'regularizacao' : 'documento',
  );
  const [erros, setErros] = useState<{ readonly arquivo?: string | undefined; readonly descricao?: string | undefined }>({});
  const [recusa, setRecusa] = useState<Recusa>(null);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = problemasDoArquivo(arquivo, descricao);
    setErros(achados);
    if (achados.arquivo || achados.descricao || arquivo === null) return;
    setRecusa(null);
    anexar.mutate(
      { documento, arquivo, descricao: descricao.trim(), finalidade },
      {
        onSuccess: () => {
          setArquivo(null);
          setDescricao('');
          if (campoDoArquivo.current) campoDoArquivo.current.value = '';
        },
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {arquivos.isPending ? (
        <EsqueletoDeLista oQue="Carregando os arquivos do documento" itens={2} icone={false} />
      ) : arquivos.isError ? (
        <ErroDaLista
          oQue="Os arquivos do documento"
          erro={arquivos.error}
          aoTentarDeNovo={() => {
            void arquivos.refetch();
          }}
        />
      ) : arquivos.data.length === 0 ? (
        <EstadoVazio
          icone={FileText}
          tom="marca"
          titulo="Nenhum arquivo neste documento"
          descricao={
            podeEnviar
              ? 'Envie a foto ou o PDF do documento. Na regularização, envie o protocolo ou o comprovante.'
              : 'Ninguém enviou a cópia deste documento. Quem cadastra documentos pode enviá-la.'
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {arquivos.data.map((a) => (
            <li key={a.id} className="flex items-baseline justify-between gap-3 border-b border-borda-sutil pb-2">
              <span className="flex min-w-0 flex-col">
                <span className="txt-corpo text-tinta">{a.descricao}</span>
                <span className="txt-apoio text-tinta-apagada">{linhaDoArquivo(a)}</span>
              </span>
              <Botao
                nivel="terciario"
                onClick={() => {
                  abrirArquivoDoDocumento(a.id).catch((falha: unknown) => {
                    setRecusa(explicarFalha(falha));
                  });
                }}
              >
                Abrir arquivo
              </Botao>
            </li>
          ))}
        </ul>
      )}

      {podeEnviar ? (
        <form onSubmit={enviar} noValidate className="flex flex-col gap-4 border-t border-borda-sutil pt-4">
          <Campo id={`${base}-finalidade`} rotulo="O que é">
            <select
              id={`${base}-finalidade`}
              className="controle w-full"
              value={finalidade}
              onChange={(e) => {
                setFinalidade(e.target.value === 'regularizacao' ? 'regularizacao' : 'documento');
              }}
            >
              {(Object.keys(ROTULO_DA_FINALIDADE) as FinalidadeDoArquivo[]).map((f) => (
                <option key={f} value={f}>
                  {ROTULO_DA_FINALIDADE[f]}
                </option>
              ))}
            </select>
          </Campo>
          <Campo id={`${base}-arquivo`} rotulo="Foto ou PDF" erro={erros.arquivo ?? null}>
            <input
              id={`${base}-arquivo`}
              ref={campoDoArquivo}
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              className="controle w-full"
              onChange={(e) => {
                const escolhido = e.target.files?.[0] ?? null;
                setArquivo(escolhido);
                setErros((atual) => ({ ...atual, arquivo: escolhido === null ? undefined : problemasDoArquivo(escolhido, 'x').arquivo }));
              }}
              {...(erros.arquivo ? { 'aria-invalid': true, 'aria-describedby': `${base}-arquivo-erro` } : {})}
            />
          </Campo>
          <Campo id={`${base}-descricao`} rotulo="Descrição" erro={erros.descricao ?? null}>
            <input
              id={`${base}-descricao`}
              type="text"
              autoComplete="off"
              maxLength={300}
              className="controle w-full"
              placeholder={finalidade === 'regularizacao' ? 'Protocolo da renovação no Detran' : 'CNH frente e verso'}
              value={descricao}
              onChange={(e) => {
                setDescricao(e.target.value);
                setErros((atual) => ({ ...atual, descricao: undefined }));
              }}
              onBlur={() => {
                setErros((atual) => ({ ...atual, descricao: problemasDoArquivo(arquivo, descricao).descricao }));
              }}
              {...(erros.descricao ? { 'aria-invalid': true, 'aria-describedby': `${base}-descricao-erro` } : {})}
            />
          </Campo>
          <div className="flex justify-end">
            <Botao nivel="primario" type="submit" disabled={anexar.isPending}>
              {anexar.isPending ? 'Enviando…' : 'Enviar arquivo'}
            </Botao>
          </div>
        </form>
      ) : null}

      {recusa !== null ? (
        <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
          <span className="txt-corpo text-tinta">{recusa.erro}</span>
          <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
        </div>
      ) : null}
    </div>
  );
}
